import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { money, dateEs, statusLabel } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, FileText, Eye } from "lucide-react";

export default function QuotesList() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      if (type !== "all") params.type = type;
      const r = await api.get("/quotes", { params });
      setRows(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, type]);

  const filtered = useMemo(() => rows, [rows]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="quotes-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pe-label">MÓDULO 1</div>
          <h1 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)]">Cotizaciones</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Producto o servicio. Historial embebido, folio único, link público con evidencia.
          </p>
        </div>
        {can("quotes_write") && (
          <Button onClick={() => navigate("/quotes/new")} className="pe-btn-primary text-white h-11" data-testid="btn-new-quote">
            <Plus size={16} className="mr-1" /> Nueva cotización
          </Button>
        )}
      </div>

      <div className="pe-card p-4 mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
          <Input placeholder="Buscar por folio, cliente o RFC…" className="pl-9 h-11"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 data-testid="input-search-quotes" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px] h-11" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="borrador">Borrador</SelectItem>
            <SelectItem value="enviada">Enviada</SelectItem>
            <SelectItem value="aceptada">Aceptada</SelectItem>
            <SelectItem value="rechazada">Rechazada</SelectItem>
            <SelectItem value="cerrada">Cerrada</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[150px] h-11" data-testid="filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo tipo</SelectItem>
            <SelectItem value="producto">Producto</SelectItem>
            <SelectItem value="servicio">Servicio</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} className="h-11" data-testid="btn-apply-filters">Aplicar</Button>
      </div>

      <div className="mt-4 pe-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[color:var(--pe-primary)] text-white text-left">
                <th className="px-4 py-3 font-bold tracking-wide text-xs uppercase">Folio</th>
                <th className="px-4 py-3 font-bold tracking-wide text-xs uppercase">Tipo</th>
                <th className="px-4 py-3 font-bold tracking-wide text-xs uppercase">Cliente</th>
                <th className="px-4 py-3 font-bold tracking-wide text-xs uppercase">Fecha</th>
                <th className="px-4 py-3 font-bold tracking-wide text-xs uppercase text-right">Total</th>
                <th className="px-4 py-3 font-bold tracking-wide text-xs uppercase">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center">
                  <FileText className="mx-auto text-slate-300" size={40} />
                  <div className="mt-3 font-bold text-slate-600">Sin cotizaciones</div>
                  <div className="text-sm text-slate-500 mt-1">
                    {can("quotes_write") ? "Crea la primera para empezar." : "Aún no hay cotizaciones."}
                  </div>
                </td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-t border-[color:var(--pe-border)] hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/quotes/${r.id}`)}
                      data-testid={`quote-row-${r.folio}`}>
                    <td className="px-4 py-3 pe-numeric font-bold text-[color:var(--pe-primary)]">{r.folio}</td>
                    <td className="px-4 py-3 capitalize">{r.type}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.client?.name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.client?.rfc || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{dateEs(r.created_at)}</td>
                    <td className="px-4 py-3 text-right pe-numeric font-bold">{money(r.total, r.currency)}</td>
                    <td className="px-4 py-3"><span className={`pe-badge pe-badge-${r.status}`}>{statusLabel[r.status]}</span></td>
                    <td className="px-4 py-3 text-right">
                      <button className="text-[color:var(--pe-accent)] hover:underline text-sm inline-flex items-center gap-1"
                              data-testid={`btn-open-${r.folio}`}>
                        <Eye size={14} /> Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
